<template>
    <DefaultHeader />

    <div class="container-fluid operators-page">

        <div class="row justify-content-center">
            <div class="col-12 col-xl-11">

                <!-- En-tête -->
                <div class="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4">

                    <div>
                        <h2 class="mb-1">
                            Gestion des opérateurs
                        </h2>

                        <p class="text-muted mb-0">
                            Gérez les comptes opérateurs ayant accès à la plateforme.
                        </p>
                    </div>

                    <button v-if="!showCreateOperator" class="btn btn-primary d-flex align-items-center gap-2"
                        @click="openCreateOperator">
                        <font-awesome-icon :icon="['fas', 'circle-plus']" />

                        Ajouter un opérateur
                    </button>

                </div>


                <!-- Statistiques -->
                <div class="row g-3 mb-4">

                    <div class="col-12 col-md-4">
                        <div class="summary-card">

                            <div class="summary-label">
                                Total
                            </div>

                            <div class="summary-value">
                                {{ operators.length }}
                            </div>

                        </div>
                    </div>

                    <div class="col-6 col-md-4">
                        <div class="summary-card">

                            <div class="summary-label">
                                Actifs
                            </div>

                            <div class="summary-value text-success">
                                {{ activeOperatorsCount }}
                            </div>

                        </div>
                    </div>

                    <div class="col-6 col-md-4">
                        <div class="summary-card">

                            <div class="summary-label">
                                Désactivés
                            </div>

                            <div class="summary-value text-secondary">
                                {{ disabledOperatorsCount }}
                            </div>

                        </div>
                    </div>

                </div>


                <!-- Messages -->
                <div v-if="successMessage" class="alert alert-success alert-dismissible fade show" role="alert">
                    {{ successMessage }}

                    <button type="button" class="btn-close" @click="successMessage = ''"></button>
                </div>

                <div v-if="errorMessage" class="alert alert-danger alert-dismissible fade show" role="alert">
                    {{ errorMessage }}

                    <button type="button" class="btn-close" @click="errorMessage = ''"></button>
                </div>


                <!-- Création -->
                <div v-if="showCreateOperator" class="card operator-create-card mb-4">
                    <div class="card-body">

                        <div class="d-flex justify-content-between align-items-start mb-4">

                            <div>
                                <h5 class="mb-1">
                                    Nouvel opérateur
                                </h5>

                                <p class="text-muted small mb-0">
                                    Créez un nouveau compte opérateur.
                                </p>
                            </div>

                            <button type="button" class="btn-close" :disabled="creatingOperator"
                                @click="closeCreateOperator"></button>

                        </div>

                        <form @submit.prevent="createOperator">

                            <div class="row g-3">

                                <!-- Prénom -->
                                <div class="col-12 col-md-6">

                                    <label for="operatorFirstName" class="form-label">
                                        Prénom
                                    </label>

                                    <input id="operatorFirstName" v-model="firstName" type="text" class="form-control"
                                        autocomplete="off" placeholder="Prénom">

                                </div>


                                <!-- Nom -->
                                <div class="col-12 col-md-6">

                                    <label for="operatorLastName" class="form-label">
                                        Nom
                                    </label>

                                    <input id="operatorLastName" v-model="lastName" type="text" class="form-control"
                                        autocomplete="off" placeholder="Nom">

                                </div>


                                <!-- Email -->
                                <div class="col-12 col-md-6">

                                    <label for="operatorEmail" class="form-label">
                                        Adresse e-mail
                                    </label>

                                    <input id="operatorEmail" v-model="email" type="text" class="form-control"
                                        autocomplete="off" placeholder="Adresse e-mail">

                                </div>


                                <!-- Mot de passe -->
                                <div class="col-12 col-md-6">

                                    <label for="operatorPassword" class="form-label">
                                        Mot de passe
                                    </label>

                                    <input id="operatorPassword" v-model="password" type="password" class="form-control"
                                        autocomplete="new-password" placeholder="Mot de passe">

                                </div>

                            </div>

                            <div class="d-flex justify-content-end gap-2 mt-4">

                                <button type="button" class="btn btn-outline-secondary" :disabled="creatingOperator"
                                    @click="closeCreateOperator">
                                    Annuler
                                </button>

                                <button type="submit" class="btn btn-primary" :disabled="creatingOperator">
                                    <span v-if="creatingOperator" class="spinner-border spinner-border-sm me-2"
                                        role="status"></span>

                                    {{
                                        creatingOperator
                                            ? 'Création...'
                                            : 'Créer l’opérateur'
                                    }}
                                </button>

                            </div>

                        </form>

                    </div>
                </div>


                <!-- Tableau -->
                <div class="card operators-table-card">

                    <div class="card-header bg-transparent">

                        <div class="d-flex justify-content-between align-items-center">

                            <div>
                                <h5 class="mb-1">
                                    Opérateurs
                                </h5>

                                <div class="text-muted small">
                                    {{ operators.length }} compte{{ operators.length > 1 ? 's' : '' }}
                                </div>
                            </div>

                            <button class="btn btn-sm btn-outline-secondary" :disabled="loading" @click="refreshData">
                                Actualiser
                            </button>

                        </div>

                    </div>


                    <!-- Chargement -->
                    <div v-if="loading" class="text-center py-5">
                        <div class="spinner-border text-primary" role="status"></div>

                        <div class="text-muted mt-3">
                            Chargement des opérateurs...
                        </div>
                    </div>


                    <!-- Tableau -->
                    <div v-else-if="operators.length > 0" class="table-responsive">

                        <table class="table align-middle mb-0 operators-table">

                            <thead>
                                <tr>
                                    <th>Opérateur</th>
                                    <th>Statut</th>
                                    <th>Dernière connexion</th>
                                    <th>Date de création</th>

                                    <th class="text-end">
                                        Actions
                                    </th>
                                </tr>
                            </thead>

                            <tbody>

                                <template v-for="operator in operators" :key="operator.id">

                                    <!-- Ligne opérateur -->
                                    <tr :class="{
                                        'operator-disabled':
                                            operator.status === 'DISABLED'
                                    }">

                                        <!-- Opérateur -->
                                        <td>

                                            <div class="d-flex align-items-center gap-3">

                                                <div class="operator-avatar" :class="{
                                                    'operator-avatar-disabled':
                                                        operator.status === 'DISABLED'
                                                }">
                                                    <font-awesome-icon :icon="['fas', 'user']" />
                                                </div>

                                                <div>

                                                    <div class="fw-semibold">
                                                        <template v-if="operator.firstName || operator.lastName">
                                                            {{ operator.firstName }}
                                                            {{ operator.lastName }}
                                                        </template>

                                                        <span v-else class="text-muted">
                                                            Nom non renseigné
                                                        </span>
                                                    </div>

                                                    <div class="text-muted small">
                                                        {{ operator.email }}
                                                    </div>

                                                    <div class="text-muted operator-id">
                                                        ID {{ operator.id }}
                                                    </div>

                                                </div>

                                            </div>

                                        </td>


                                        <!-- Statut -->
                                        <td>

                                            <span v-if="operator.status === 'ACTIVE'"
                                                class="badge rounded-pill text-bg-success">
                                                Actif
                                            </span>

                                            <span v-else class="badge rounded-pill text-bg-secondary">
                                                Désactivé
                                            </span>

                                        </td>


                                        <!-- Dernière connexion -->
                                        <td>

                                            <template v-if="operator.lastConnection">

                                                <div>
                                                    {{
                                                        $filters.secondDateFormat(
                                                            operator.lastConnection
                                                        )
                                                    }}
                                                </div>

                                                <div class="text-muted small">
                                                    {{
                                                        $filters.timeAgo(
                                                            operator.lastConnection
                                                        )
                                                    }}
                                                </div>

                                            </template>

                                            <span v-else class="text-muted">
                                                Jamais connecté
                                            </span>

                                        </td>


                                        <!-- Création -->
                                        <td>

                                            <span v-if="operator.createdAt">
                                                {{
                                                    $filters.secondDateFormat(
                                                        operator.createdAt
                                                    )
                                                }}
                                            </span>

                                            <span v-else class="text-muted">
                                                -
                                            </span>

                                        </td>


                                        <!-- Actions -->
                                        <td class="text-end">

                                            <div class="d-flex justify-content-end gap-2">

                                                <button v-if="operator.status === 'ACTIVE'"
                                                    class="btn btn-sm btn-outline-primary"
                                                    @click="toggleEditOperator(operator)">
                                                    {{
                                                        editingOperatorId === operator.id
                                                            ? 'Fermer'
                                                            : 'Modifier'
                                                    }}
                                                </button>

                                                <button v-if="operator.status !== 'DISABLED'"
                                                    class="btn btn-sm btn-outline-danger"
                                                    @click="deleteOperator(operator)">
                                                    <font-awesome-icon :icon="['fas', 'ban']" class="me-1" />

                                                    Désactiver
                                                </button>

                                                <span v-else class="text-muted small d-flex align-items-center">
                                                    Compte désactivé
                                                </span>

                                            </div>

                                        </td>

                                    </tr>


                                    <!-- Modification -->
                                    <tr v-if="editingOperatorId === operator.id" class="operator-edit-row">
                                        <td colspan="5">

                                            <form class="operator-edit-form" @submit.prevent="updateOperator(operator)">

                                                <div class="row g-3 align-items-end">

                                                    <!-- Prénom -->
                                                    <div class="col-12 col-md-3">

                                                        <label class="form-label small">
                                                            Prénom
                                                        </label>

                                                        <input v-model="editFirstName" type="text" class="form-control"
                                                            placeholder="Prénom">

                                                    </div>


                                                    <!-- Nom -->
                                                    <div class="col-12 col-md-3">

                                                        <label class="form-label small">
                                                            Nom
                                                        </label>

                                                        <input v-model="editLastName" type="text" class="form-control"
                                                            placeholder="Nom">

                                                    </div>


                                                    <!-- Email -->
                                                    <div class="col-12 col-md-4">

                                                        <label class="form-label small">
                                                            Adresse e-mail
                                                        </label>

                                                        <input v-model="editEmail" type="text" class="form-control"
                                                            placeholder="Adresse e-mail">

                                                    </div>


                                                    <!-- Actions édition -->
                                                    <div class="col-12 col-md-2">

                                                        <div class="d-flex gap-2">

                                                            <button type="button"
                                                                class="btn btn-outline-secondary flex-grow-1"
                                                                :disabled="updatingOperator" @click="closeEditOperator">
                                                                Annuler
                                                            </button>

                                                            <button type="submit" class="btn btn-primary flex-grow-1"
                                                                :disabled="updatingOperator">
                                                                <span v-if="updatingOperator"
                                                                    class="spinner-border spinner-border-sm"
                                                                    role="status"></span>

                                                                <span v-else>
                                                                    Enregistrer
                                                                </span>
                                                            </button>

                                                        </div>

                                                    </div>

                                                </div>

                                            </form>

                                        </td>
                                    </tr>

                                </template>

                            </tbody>

                        </table>

                    </div>


                    <!-- Aucun opérateur -->
                    <div v-else class="empty-state">
                        <div class="empty-state-icon">
                            <font-awesome-icon :icon="['fas', 'user']" />
                        </div>

                        <h5>
                            Aucun opérateur
                        </h5>

                        <p class="text-muted mb-3">
                            Aucun compte opérateur n’a encore été créé.
                        </p>

                        <button class="btn btn-primary" @click="openCreateOperator">
                            <font-awesome-icon :icon="['fas', 'circle-plus']" class="me-1" />

                            Ajouter un opérateur
                        </button>
                    </div>

                </div>

            </div>
        </div>

    </div>

    <DefaultFooter />
</template>


<script>
import DefaultHeader from '@/components/DefaultHeader.vue'
import DefaultFooter from '@/components/DefaultFooter.vue'

import UserService from '@/services/user.service'
import AuthService from '@/services/auth.service'

export default {

    components: {
        DefaultHeader,
        DefaultFooter
    },

    data() {
        return {
            operators: [],

            showCreateOperator: false,
            creatingOperator: false,
            updatingOperator: false,
            loading: false,

            email: '',
            password: '',
            firstName: '',
            lastName: '',

            editingOperatorId: null,
            editEmail: '',
            editFirstName: '',
            editLastName: '',

            successMessage: '',
            errorMessage: ''
        };
    },

    computed: {

        activeOperatorsCount() {
            return this.operators.filter(
                operator => operator.status === 'ACTIVE'
            ).length;
        },

        disabledOperatorsCount() {
            return this.operators.filter(
                operator => operator.status === 'DISABLED'
            ).length;
        }

    },

    mounted() {
        this.refreshData();
    },

    methods: {

        refreshData() {

            this.loading = true;
            this.errorMessage = '';

            UserService.getOperators()
                .then(response => {

                    this.operators =
                        response.data || [];

                })
                .catch(e => {

                    console.log(e);

                    this.errorMessage =
                        e.response?.data?.message ||
                        "Impossible de récupérer la liste des opérateurs.";

                })
                .finally(() => {

                    this.loading = false;

                });
        },


        openCreateOperator() {

            this.resetCreateForm();
            this.closeEditOperator();

            this.successMessage = '';
            this.errorMessage = '';

            this.showCreateOperator = true;
        },


        closeCreateOperator() {

            this.resetCreateForm();

            this.showCreateOperator = false;
        },


        resetCreateForm() {

            this.email = '';
            this.password = '';
            this.firstName = '';
            this.lastName = '';
        },


        async createOperator() {

            this.creatingOperator = true;
            this.successMessage = '';
            this.errorMessage = '';

            try {

                await AuthService.createOperator(
                    this.email,
                    this.password,
                    this.firstName,
                    this.lastName
                );

                const operatorEmail =
                    this.email;

                this.closeCreateOperator();

                this.successMessage =
                    `L'opérateur ${operatorEmail} a été créé avec succès.`;

                this.refreshData();

            } catch (e) {

                console.log(e);

                this.errorMessage =
                    e.response?.data?.message ||
                    "Impossible de créer l'opérateur.";

            } finally {

                this.creatingOperator = false;

            }
        },


        toggleEditOperator(operator) {

            if (this.editingOperatorId === operator.id) {
                this.closeEditOperator();
                return;
            }

            this.successMessage = '';
            this.errorMessage = '';

            this.editingOperatorId =
                operator.id;

            this.editEmail =
                operator.email || '';

            this.editFirstName =
                operator.firstName || '';

            this.editLastName =
                operator.lastName || '';
        },


        closeEditOperator() {

            this.editingOperatorId = null;

            this.editEmail = '';
            this.editFirstName = '';
            this.editLastName = '';
        },


        async updateOperator(operator) {

            this.updatingOperator = true;
            this.successMessage = '';
            this.errorMessage = '';

            try {

                await UserService.updateOperator(
                    operator.id,
                    this.editEmail,
                    this.editFirstName,
                    this.editLastName
                );

                const operatorEmail =
                    this.editEmail;

                this.closeEditOperator();

                this.successMessage =
                    `L'opérateur ${operatorEmail} a été mis à jour.`;

                this.refreshData();

            } catch (e) {

                console.log(e);

                this.errorMessage =
                    e.response?.data?.message ||
                    "Impossible de modifier l'opérateur.";

            } finally {

                this.updatingOperator = false;

            }
        },


        deleteOperator(operator) {

            if (
                !confirm(
                    `Désactiver l'opérateur ${operator.email} ?`
                )
            ) {
                return;
            }

            this.successMessage = '';
            this.errorMessage = '';

            UserService.deleteOperator(
                operator.id
            )
                .then(() => {

                    if (
                        this.editingOperatorId === operator.id
                    ) {
                        this.closeEditOperator();
                    }

                    this.successMessage =
                        `L'opérateur ${operator.email} a été désactivé.`;

                    this.refreshData();

                })
                .catch(e => {

                    console.log(e);

                    this.errorMessage =
                        e.response?.data?.message ||
                        "Impossible de désactiver l'opérateur.";

                });
        }

    }
}
</script>


<style scoped>
.operators-page {
    padding-top: 1.5rem;
    padding-bottom: 3rem;
}

.summary-card {
    height: 100%;
    padding: 1.15rem 1.25rem;
    background: var(--bs-body-bg);
    border: 1px solid var(--bs-border-color);
    border-radius: 0.75rem;
}

.summary-label {
    margin-bottom: 0.25rem;
    font-size: 0.8rem;
    font-weight: 600;
    color: var(--bs-secondary-color);
    text-transform: uppercase;
    letter-spacing: 0.04em;
}

.summary-value {
    font-size: 1.65rem;
    font-weight: 700;
    line-height: 1.2;
}

.operator-create-card,
.operators-table-card {
    border: 1px solid var(--bs-border-color);
    border-radius: 0.85rem;
    overflow: hidden;
}

.operator-create-card {
    background: var(--bs-tertiary-bg);
}

.operators-table-card .card-header {
    padding: 1.1rem 1.25rem;
    border-bottom: 1px solid var(--bs-border-color);
}

.operators-table thead th {
    padding: 0.85rem 1rem;
    background: var(--bs-tertiary-bg);
    border-bottom: 1px solid var(--bs-border-color);
    color: var(--bs-secondary-color);
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    white-space: nowrap;
}

.operators-table tbody td {
    padding: 1rem;
    vertical-align: middle;
}

.operator-avatar {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    width: 2.5rem;
    height: 2.5rem;
    border-radius: 50%;
    background: var(--bs-primary-bg-subtle);
    color: var(--bs-primary);
}

.operator-avatar-disabled {
    background: var(--bs-secondary-bg);
    color: var(--bs-secondary-color);
}

.operator-disabled>td {
    background: var(--bs-tertiary-bg);
}

.operator-disabled .fw-semibold {
    color: var(--bs-secondary-color);
}

.operator-id {
    margin-top: 0.1rem;
    font-size: 0.72rem;
}

.operator-edit-row>td {
    padding: 0 !important;
    background: var(--bs-tertiary-bg);
    border-top: 0;
}

.operator-edit-form {
    padding: 1.25rem;
    border-bottom: 1px solid var(--bs-border-color);
}

.operator-edit-form .form-label {
    margin-bottom: 0.4rem;
    font-weight: 600;
    color: var(--bs-secondary-color);
}

.empty-state {
    padding: 4rem 1.5rem;
    text-align: center;
}

.empty-state-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 3.5rem;
    height: 3.5rem;
    margin: 0 auto 1rem;
    border-radius: 50%;
    background: var(--bs-secondary-bg);
    color: var(--bs-secondary-color);
    font-size: 1.25rem;
}

@media (max-width: 767.98px) {

    .operators-page {
        padding-top: 1rem;
    }

    .operators-table tbody td,
    .operators-table thead th {
        padding-left: 0.75rem;
        padding-right: 0.75rem;
    }

}
</style>